import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { Plus, Users, Pencil, Trash2, X, Check, UserPlus, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, DataTable, DetailDrawer, Dialog, Field, SearchInput, OverflowMenu, PersonAvatar, ErrorState,
  num, plural,
} from '../../components/ui';
import { roleLabel, useStableCallback } from '../Staff/hrKit';

const COLORS = [
  { hex: '#6366f1', name: 'Indigo' }, { hex: '#22c55e', name: 'Green' }, { hex: '#f59e0b', name: 'Amber' },
  { hex: '#ef4444', name: 'Red' }, { hex: '#8b5cf6', name: 'Violet' }, { hex: '#14b8a6', name: 'Teal' },
  { hex: '#ec4899', name: 'Pink' }, { hex: '#0ea5e9', name: 'Sky' },
];
const personName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim();

function TeamDot({ color, className }) {
  return <span className={clsx('inline-block h-2.5 w-2.5 shrink-0 rounded-full', className)} style={{ background: color || '#94a3b8' }} aria-hidden="true" />;
}

/* ── New / edit team ──────────────────────────────────────────────────── */

function TeamDialog({ open, team, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', color: COLORS[0].hex });
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError(''); setSaveError('');
    setForm(team ? { name: team.name || '', description: team.description || '', color: team.color || COLORS[0].hex } : { name: '', description: '', color: COLORS[0].hex });
  }, [open, team]);
  const close = useStableCallback(() => { if (!saving) onClose(); });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Give the team a name.'); return; }
    setSaving(true); setSaveError('');
    try {
      if (team) await api.put(`/teams/${team.id}`, { ...form, name: form.name.trim() });
      else await api.post('/teams', { ...form, name: form.name.trim() });
      toast.success(team ? 'Team updated' : 'Team created');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setSaveError(err.response?.data?.message || "The team wasn't saved. Try again.");
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} size="sm" title={team ? 'Edit team' : 'New team'}
      footer={(
        <>
          {saveError && <span className="mr-auto text-[13px] text-rose-700" role="alert">{saveError}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="team-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : team ? 'Save changes' : 'Create team'}</button>
        </>
      )}>
      <form id="team-form" onSubmit={save} className="space-y-4">
        <Field label="Name" required error={error}>
          {(id) => <input id={id} className="input-field" value={form.name} onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setError(''); }} />}
        </Field>
        <Field label="Description">
          {(id) => <textarea id={id} className="input-field" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />}
        </Field>
        <fieldset>
          <legend className="mb-1 text-[13px] font-medium text-slate-700">Colour</legend>
          <p className="mb-2 text-[12px] text-slate-500">Shown as a dot beside the team's name.</p>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => {
              const on = form.color === c.hex;
              return (
                <button key={c.hex} type="button" onClick={() => setForm((f) => ({ ...f, color: c.hex }))}
                  aria-label={c.name} aria-pressed={on} title={c.name}
                  className={clsx('inline-flex h-9 w-9 items-center justify-center rounded-lg ring-2 ring-offset-2 transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-primary-500',
                    on ? 'ring-slate-900' : 'ring-transparent')}
                  style={{ background: c.hex }}>
                  {on && <Check size={15} className="text-white" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </fieldset>
      </form>
    </Dialog>
  );
}

/* ── Members drawer ───────────────────────────────────────────────────── */

function Picker({ label, placeholder, results, loading, onAdd, query, onQuery, emptyText }) {
  return (
    <div className="relative">
      <SearchInput value={query} onChange={onQuery} placeholder={placeholder} />
      {query.trim().length > 0 && (
        <ul aria-label={label} className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-card-deep">
          {loading && <li className="flex items-center gap-2 px-3 py-2 text-[13px] text-slate-500"><Loader2 size={13} className="animate-spin" aria-hidden="true" />Searching…</li>}
          {!loading && results.length === 0 && <li className="px-3 py-2 text-[13px] text-slate-500">{emptyText}</li>}
          {!loading && results.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => onAdd(r)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50">
                <UserPlus size={13} className="shrink-0 text-slate-400" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{personName(r)}</span>
                {r.sub && <span className="shrink-0 text-[12px] text-slate-500">{r.sub}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Chips({ items, onRemove, empty, photos }) {
  if (!items.length) return <p className="text-[13px] text-slate-500">{empty}</p>;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((p) => (
        <li key={p.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-1 pr-1 text-[12.5px] text-slate-700">
          <PersonAvatar id={p.id} photo={photos && photos[p.id]} firstName={p.first_name} lastName={p.last_name} size="xs" />
          <span className="truncate">{personName(p)}</span>
          <button type="button" onClick={() => onRemove(p)} aria-label={`Remove ${personName(p)}`}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600">
            <X size={12} aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function MembersDrawer({ team, staffList, photos, onClose, onSaved }) {
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [staffQ, setStaffQ] = useState('');
  const [clientQ, setClientQ] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const timer = useRef(null);
  const confirm = useConfirm();

  const load = useCallback(() => {
    if (!team) return;
    setLoading(true); setLoadError(null); setDirty(false); setSaveError('');
    api.get(`/teams/${team.id}/members`)
      .then((r) => { setStaff(r.data?.staff || []); setClients(r.data?.clients || []); })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load this team's members. Try again."))
      .finally(() => setLoading(false));
  }, [team]);
  useEffect(() => { setStaffQ(''); setClientQ(''); load(); }, [load]);

  // Clients are searched on the server — an organisation can have thousands.
  useEffect(() => {
    clearTimeout(timer.current);
    const q = clientQ.trim();
    if (!q) { setClientResults([]); return undefined; }
    setClientLoading(true);
    timer.current = setTimeout(() => {
      api.get(`/clients?search=${encodeURIComponent(q)}&limit=20`)
        .then((r) => setClientResults(Array.isArray(r.data?.clients) ? r.data.clients : (Array.isArray(r.data) ? r.data : [])))
        .catch(() => setClientResults([]))
        .finally(() => setClientLoading(false));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [clientQ]);

  const staffIds = new Set(staff.map((s) => s.id));
  const clientIds = new Set(clients.map((c) => c.id));
  const staffResults = staffQ.trim()
    ? staffList.filter((s) => !staffIds.has(s.id) && `${personName(s)} ${s.email || ''}`.toLowerCase().includes(staffQ.trim().toLowerCase()))
      .slice(0, 20).map((s) => ({ ...s, sub: roleLabel(s.role) }))
    : [];
  const clientOptions = clientResults.filter((c) => !clientIds.has(c.id)).map((c) => ({ ...c, sub: c.ndis_number || '' }));

  const requestClose = useStableCallback(async () => {
    if (saving) return;
    if (dirty && !await confirm('Your changes to this team\'s members will be lost.', { title: 'Discard changes?', confirmLabel: 'Discard', danger: true })) return;
    onClose();
  });

  const save = async () => {
    setSaving(true); setSaveError('');
    try {
      await Promise.all([
        api.post(`/teams/${team.id}/staff`, { staffIds: staff.map((s) => s.id) }),
        api.post(`/teams/${team.id}/clients`, { clientIds: clients.map((c) => c.id) }),
      ]);
      toast.success(`${team.name}: ${plural(staff.length, 'staff member')}, ${plural(clients.length, 'client')}`);
      setDirty(false);
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaveError(e.response?.data?.message || "The members weren't saved. Try again.");
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      open={!!team}
      onClose={requestClose}
      width="md"
      title={team ? team.name : ''}
      subtitle={team && team.description}
      statusNode={team ? <TeamDot color={team.color} /> : null}
      meta={[{ label: 'Staff', value: num(staff.length) }, { label: 'Clients', value: num(clients.length) }]}
      footer={(
        <>
          {saveError && <span className="mr-auto text-[13px] text-rose-700" role="alert">{saveError}</span>}
          {!saveError && dirty && <span className="mr-auto flex items-center gap-1.5 text-[13px] font-medium text-amber-700"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />Unsaved changes</span>}
          <button type="button" onClick={requestClose} className="btn-secondary btn-sm" disabled={saving}>Cancel</button>
          <button type="button" onClick={save} className="btn-primary btn-sm" disabled={saving || loading || !!loadError || !dirty}>{saving ? 'Saving…' : 'Save members'}</button>
        </>
      )}
    >
      {loading ? (
        <div className="space-y-3" aria-busy="true"><div className="skeleton h-8 w-full" /><div className="skeleton h-16 w-full" /></div>
      ) : loadError ? <ErrorState message={loadError} onRetry={load} /> : (
        <div className="space-y-6">
          <section aria-labelledby="team-staff-h" className="space-y-2">
            <h4 id="team-staff-h" className="text-[13px] font-semibold text-slate-700">Staff ({num(staff.length)})</h4>
            <Picker label="Matching staff" placeholder="Add staff…" query={staffQ} onQuery={setStaffQ} results={staffResults}
              emptyText="No one else matches." onAdd={(s) => { setStaff((l) => [...l, s]); setDirty(true); setStaffQ(''); }} />
            <Chips items={staff} photos={photos} empty="No staff in this team yet." onRemove={(s) => { setStaff((l) => l.filter((x) => x.id !== s.id)); setDirty(true); }} />
          </section>
          <section aria-labelledby="team-clients-h" className="space-y-2">
            <h4 id="team-clients-h" className="text-[13px] font-semibold text-slate-700">Clients ({num(clients.length)})</h4>
            <Picker label="Matching clients" placeholder="Add clients…" query={clientQ} onQuery={setClientQ} results={clientOptions} loading={clientLoading}
              emptyText="No clients match that search." onAdd={(c) => { setClients((l) => [...l, c]); setDirty(true); setClientQ(''); }} />
            <Chips items={clients} empty="No clients in this team yet." onRemove={(c) => { setClients((l) => l.filter((x) => x.id !== c.id)); setDirty(true); }} />
          </section>
        </div>
      )}
    </DetailDrawer>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function Teams() {
  const confirm = useConfirm();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [editing, setEditing] = useState(null);   // null | 'new' | team
  const [membersOf, setMembersOf] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get('/teams')
      .then((r) => setTeams(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load teams. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/staff').then((r) => setStaffList(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
  }, []);
  const photos = useMemo(() => Object.fromEntries(staffList.map((s) => [s.id, s.profile_photo_url])), [staffList]);

  const deleteTeam = async (t) => {
    const ok = await confirm(
      `${plural(Number(t.staff_count) || 0, 'staff member')} and ${plural(Number(t.client_count) || 0, 'client')} will be taken out of this team. Nobody is deleted.`,
      { title: `Delete ${t.name}?`, confirmLabel: 'Delete team', danger: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/teams/${t.id}`);
      toast.success(`${t.name} deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Only an administrator can delete a team.' : (e.response?.data?.message || "The team wasn't deleted. Try again."));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? teams.filter((t) => `${t.name} ${t.description || ''}`.toLowerCase().includes(q)) : teams;
  }, [teams, search]);

  const columns = [
    { key: 'name', header: 'Team', sortable: true, sortValue: (t) => (t.name || '').toLowerCase(), render: (t) => (
      <div className="flex min-w-0 items-start gap-2.5">
        <TeamDot color={t.color} className="mt-1.5" />
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{t.name}</p>
          {t.description && <p className="mt-0.5 max-w-md truncate text-[12.5px] text-slate-500" title={t.description}>{t.description}</p>}
        </div>
      </div>
    ) },
    { key: 'staff', header: 'Staff', sortable: true, sortValue: (t) => Number(t.staff_count) || 0, defaultDir: 'desc', render: (t) => {
      const preview = Array.isArray(t.staff_preview) ? t.staff_preview : [];
      const total = Number(t.staff_count) || 0;
      if (!total) return <span className="text-slate-500">No staff</span>;
      return (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5" aria-hidden="true">
            {preview.map((p) => (
              <PersonAvatar key={p.id} id={p.id} photo={photos[p.id]} firstName={p.first_name} lastName={p.last_name} size="xs" className="ring-2 ring-white" />
            ))}
          </div>
          <span className="whitespace-nowrap text-[12.5px] tabular-nums text-slate-600">
            {total > preview.length ? `+${num(total - preview.length)} · ` : ''}{plural(total, 'person', 'people')}
          </span>
          <span className="sr-only">{preview.map(personName).join(', ')}</span>
        </div>
      );
    } },
    { key: 'clients', header: 'Clients', align: 'right', sortable: true, defaultDir: 'desc', sortValue: (t) => Number(t.client_count) || 0,
      render: (t) => <span className="tabular-nums">{num(t.client_count || 0)}</span> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (t) => (
      <span className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => setMembersOf(t)} className="btn-secondary btn-sm">Members</button>
        <OverflowMenu label={`More actions for ${t.name}`} items={[
          { label: 'Edit team', icon: Pencil, onClick: () => setEditing(t) },
          { label: 'Manage members', icon: Users, onClick: () => setMembersOf(t) },
          { divider: true },
          { label: 'Delete team', icon: Trash2, danger: true, onClick: () => deleteTeam(t) },
        ]} />
      </span>
    ) },
  ];

  return (
    <div className={PAGE_WIDE}>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3">
          <span className="text-[13px] text-slate-600">{loading ? 'Loading…' : plural(teams.length, 'team')}</span>
          {teams.length > 6 && <SearchInput value={search} onChange={setSearch} placeholder="Search teams" className="w-full sm:w-60" />}
          <button type="button" onClick={() => setEditing('new')} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />New team</button>
        </div>
        <DataTable
          label="Teams"
          columns={columns}
          rows={filtered}
          loading={loading}
          error={loadError}
          onRetry={load}
          defaultSort={{ key: 'name', dir: 'asc' }}
          onRowClick={(t) => setMembersOf(t)}
          empty={teams.length === 0
            ? { icon: Users, title: 'No teams yet', hint: 'Group staff and clients into care teams so you can roster and report by team.', action: <button type="button" onClick={() => setEditing('new')} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />Create your first team</button> }
            : { icon: Users, title: 'No teams match', hint: 'Try another search.', action: <button type="button" onClick={() => setSearch('')} className="btn-secondary btn-sm">Clear search</button> }}
        />
      </div>

      <TeamDialog open={editing !== null} team={editing && editing !== 'new' ? editing : null} onClose={() => setEditing(null)} onSaved={load} />
      <MembersDrawer team={membersOf} staffList={staffList} photos={photos} onClose={() => setMembersOf(null)} onSaved={load} />
    </div>
  );
}
